import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { Layout } from '@/components/Layout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ToastProvider } from '@/components/Toast'
import { CookieConsent } from '@/components/CookieConsent'
import { ThemeProvider } from '@/lib/theme'
import { I18nProvider } from '@/i18n/index'
import { Suspense, lazy, type ReactNode } from 'react'

const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const GoLive = lazy(() => import('@/pages/GoLive').then(m => ({ default: m.GoLive })))
const Chat = lazy(() => import('@/pages/Chat').then(m => ({ default: m.Chat })))
const MultiChat = lazy(() => import('@/pages/MultiChat').then(m => ({ default: m.MultiChat })))
const Analytics = lazy(() => import('@/pages/Analytics').then(m => ({ default: m.Analytics })))
const AnalyticsDashboard = lazy(() => import('@/pages/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })))
const Alerts = lazy(() => import('@/pages/Alerts').then(m => ({ default: m.Alerts })))
const AlertEditor = lazy(() => import('@/pages/AlertEditor').then(m => ({ default: m.AlertEditor })))
const Highlights = lazy(() => import('@/pages/Highlights').then(m => ({ default: m.Highlights })))
const StreamHealth = lazy(() => import('@/pages/StreamHealth').then(m => ({ default: m.StreamHealth })))
const Sponsors = lazy(() => import('@/pages/Sponsors').then(m => ({ default: m.Sponsors })))
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })))
const Collab = lazy(() => import('@/pages/Collab').then(m => ({ default: m.Collab })))
const Teams = lazy(() => import('@/pages/Teams').then(m => ({ default: m.Teams })))
const Coaching = lazy(() => import('@/pages/Coaching').then(m => ({ default: m.Coaching })))
const Intelligence = lazy(() => import('@/pages/Intelligence').then(m => ({ default: m.Intelligence })))
const Notifications = lazy(() => import('@/pages/Notifications').then(m => ({ default: m.Notifications })))
const Schedule = lazy(() => import('@/pages/Schedule').then(m => ({ default: m.Schedule })))
const Calendar = lazy(() => import('@/pages/Calendar').then(m => ({ default: m.Calendar })))
const Points = lazy(() => import('@/pages/Points').then(m => ({ default: m.Points })))
const Polls = lazy(() => import('@/pages/Polls').then(m => ({ default: m.Polls })))
const Clips = lazy(() => import('@/pages/Clips').then(m => ({ default: m.Clips })))
const ClipImport = lazy(() => import('@/pages/ClipImport').then(m => ({ default: m.ClipImport })))
const Vods = lazy(() => import('@/pages/Vods').then(m => ({ default: m.Vods })))
const Studio = lazy(() => import('@/pages/Studio').then(m => ({ default: m.Studio })))
const StreamSetup = lazy(() => import('@/pages/StreamSetup').then(m => ({ default: m.StreamSetup })))
const WidgetBuilder = lazy(() => import('@/pages/WidgetBuilder').then(m => ({ default: m.WidgetBuilder })))
const OverlayManager = lazy(() => import('@/pages/OverlayManager').then(m => ({ default: m.OverlayManager })))
const Marketplace = lazy(() => import('@/pages/Marketplace').then(m => ({ default: m.Marketplace })))
const Themes = lazy(() => import('@/pages/Themes').then(m => ({ default: m.Themes })))
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then(m => ({ default: m.AuthCallback })))
const Register = lazy(() => import('@/pages/Register').then(m => ({ default: m.Register })))
const TipPage = lazy(() => import('@/pages/TipPage').then(m => ({ default: m.TipPage })))
const Giveaways = lazy(() => import('@/pages/Giveaways').then(m => ({ default: m.Giveaways })))
const Contests = lazy(() => import('@/pages/Contests').then(m => ({ default: m.Contests })))
const MediaRequests = lazy(() => import('@/pages/MediaRequests').then(m => ({ default: m.MediaRequests })))
const Goals = lazy(() => import('@/pages/Goals').then(m => ({ default: m.Goals })))
const StreamGoals = lazy(() => import('@/pages/StreamGoals').then(m => ({ default: m.StreamGoals })))
const Landing = lazy(() => import('@/pages/Landing').then(m => ({ default: m.Landing })))
const Loyalty = lazy(() => import('@/pages/Loyalty').then(m => ({ default: m.Loyalty })))
const Counters = lazy(() => import('@/pages/Counters').then(m => ({ default: m.Counters })))
const Multistream = lazy(() => import('@/pages/Multistream').then(m => ({ default: m.Multistream })))
const Emotes = lazy(() => import('@/pages/Emotes').then(m => ({ default: m.Emotes })))
const Activity = lazy(() => import('@/pages/Activity').then(m => ({ default: m.Activity })))
const ChatBot = lazy(() => import('@/pages/ChatBot').then(m => ({ default: m.ChatBot })))
const Moderation = lazy(() => import('@/pages/Moderation').then(m => ({ default: m.Moderation })))
const Billing = lazy(() => import('@/pages/Billing').then(m => ({ default: m.Billing })))
const Webhooks = lazy(() => import('@/pages/Webhooks').then(m => ({ default: m.Webhooks })))
const TipHistory = lazy(() => import('@/pages/TipHistory').then(m => ({ default: m.TipHistory })))
const ChatOverlay = lazy(() => import('@/pages/ChatOverlay').then(m => ({ default: m.ChatOverlay })))
const Connections = lazy(() => import('@/pages/Connections').then(m => ({ default: m.Connections })))
const Subscribers = lazy(() => import('@/pages/Subscribers').then(m => ({ default: m.Subscribers })))
const StreamKeys = lazy(() => import('@/pages/StreamKeys').then(m => ({ default: m.StreamKeys })))
const ApiDocs = lazy(() => import('@/pages/ApiDocs').then(m => ({ default: m.ApiDocs })))
const Profile = lazy(() => import('@/pages/Profile').then(m => ({ default: m.Profile })))
const Discord = lazy(() => import('@/pages/Discord'))
const Leaderboard = lazy(() => import('@/pages/Leaderboard'))
const MerchStore = lazy(() => import('@/pages/MerchStore'))
const Music = lazy(() => import('@/pages/Music'))
const Hotkeys = lazy(() => import('@/pages/Hotkeys'))
const Raids = lazy(() => import('@/pages/Raids'))
const RaidTools = lazy(() => import('@/pages/RaidTools'))
const Badges = lazy(() => import('@/pages/Badges'))
const WatchParty = lazy(() => import('@/pages/WatchParty'))
const CoStreaming = lazy(() => import('@/pages/CoStreaming'))
const Soundboard = lazy(() => import('@/pages/Soundboard'))
const RecentEvents = lazy(() => import('@/pages/RecentEvents'))
const ChatCommands = lazy(() => import('@/pages/ChatCommands'))
const ChatTimers = lazy(() => import('@/pages/ChatTimers'))
const AutoMod = lazy(() => import('@/pages/AutoMod'))
const ViewerRoles = lazy(() => import('@/pages/ViewerRoles'))
const Follows = lazy(() => import('@/pages/Follows'))
const ImportExport = lazy(() => import('@/pages/ImportExport'))
const DataExport = lazy(() => import('@/pages/DataExport').then(m => ({ default: m.DataExport })))
const StreamInfo = lazy(() => import('@/pages/StreamInfo'))
const Redemptions = lazy(() => import('@/pages/Redemptions'))
const Quotes = lazy(() => import('@/pages/Quotes').then(m => ({ default: m.Quotes })))
const ViewerQueue = lazy(() => import('@/pages/ViewerQueue').then(m => ({ default: m.ViewerQueue })))
const ChatStats = lazy(() => import('@/pages/ChatStats'))
const Sessions = lazy(() => import('@/pages/Sessions'))
const StreamReport = lazy(() => import('@/pages/StreamReport'))
const PublicStats = lazy(() => import('@/pages/PublicStats'))
const Privacy = lazy(() => import('@/pages/Privacy').then(m => ({ default: m.Privacy })))
const Terms = lazy(() => import('@/pages/Terms').then(m => ({ default: m.Terms })))
const Support = lazy(() => import('@/pages/Support').then(m => ({ default: m.Support })))
const Pricing = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.Pricing })))
const Features = lazy(() => import('@/pages/Features').then(m => ({ default: m.Features })))
const About = lazy(() => import('@/pages/About').then(m => ({ default: m.About })))
const LiveDashboard = lazy(() => import('@/pages/LiveDashboard').then(m => ({ default: m.LiveDashboard })))
const StreamCommand = lazy(() => import('@/pages/StreamCommand').then(m => ({ default: m.StreamCommand })))
const Compare = lazy(() => import('@/pages/Compare').then(m => ({ default: m.Compare })))
const FromStreamElements = lazy(() => import('@/pages/FromStreamElements').then(m => ({ default: m.FromStreamElements })))
const ObsDock = lazy(() => import('@/pages/ObsDock').then(m => ({ default: m.ObsDock })))
const ObsSetup = lazy(() => import('@/pages/ObsSetup').then(m => ({ default: m.ObsSetup })))
const Embed = lazy(() => import('@/pages/Embed').then(m => ({ default: m.Embed })))
const EmbedCode = lazy(() => import('@/pages/EmbedCode').then(m => ({ default: m.EmbedCode })))
const OverlayEditor = lazy(() => import('@/pages/OverlayEditor').then(m => ({ default: m.OverlayEditor })))
const OverlayScene = lazy(() => import('@/pages/OverlayScene').then(m => ({ default: m.OverlayScene })))
const Discover = lazy(() => import('@/pages/Discover').then(m => ({ default: m.Discover })))
const DevDocs = lazy(() => import('@/pages/DevDocs').then(m => ({ default: m.DevDocs })))
const Watch = lazy(() => import('@/pages/Watch'))
const Channel = lazy(() => import('@/pages/Channel'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center p-12">
      <div
        className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
        style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
      />
    </div>
  )
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
        />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function NotFound() {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-8 border border-white/10 text-center"
        style={{ backgroundColor: 'var(--bg-card)' }}
      >
        <div
          aria-hidden="true"
          className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-6"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          ?
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">Page not found</h1>
        <p className="text-gray-400 text-sm mb-6">
          The page you are looking for does not exist or has been moved.
        </p>
        <Link
          to="/dashboard"
          className="inline-block px-6 py-2.5 rounded-xl text-white font-medium text-sm transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  )
}

function AppRoutes() {
  const location = useLocation()
  return (
    <ErrorBoundary key={location.pathname}>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/tip/:slug" element={<TipPage />} />
        <Route path="/stats/:slug" element={<PublicStats />} />
        {/* Watch page — public viewer experience with player + chat */}
        <Route path="/watch/:streamKey" element={<Watch />} />
        <Route path="/channel/:slug" element={<Channel />} />
        {/* Embed player — no chrome, safe to iframe on external sites */}
        <Route path="/embed/:streamKey" element={<Embed />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/about" element={<About />} />
        <Route path="/dev-docs" element={<DevDocs />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/from-streamelements" element={<FromStreamElements />} />
        {/* OBS integration — token-auth, no session wrapper */}
        <Route path="/obs-dock" element={<ObsDock />} />
        <Route path="/obs-setup" element={<ObsSetup />} />
        {/* Overlay scene renderer — public, used as OBS browser source */}
        <Route path="/overlay/scene/:id" element={<OverlayScene />} />
        {/* Chat is publicly accessible (read-only for anon users) */}
        <Route element={<Layout />}>
          <Route path="/chat" element={<Chat />} />
          <Route path="/chat/multi" element={<MultiChat />} />
          {/* API docs are public so developers can evaluate before signing up */}
          <Route path="/api-docs" element={<ApiDocs />} />
        </Route>
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/setup" element={<StreamSetup />} />
          <Route path="/live" element={<GoLive />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/analytics" element={<AnalyticsDashboard />} />
          <Route path="/analytics/legacy" element={<Analytics />} />
          <Route path="/highlights" element={<Highlights />} />
          <Route path="/health" element={<StreamHealth />} />
          <Route path="/live-dashboard" element={<LiveDashboard />} />
          <Route path="/command" element={<StreamCommand />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/alerts/editor" element={<AlertEditor />} />
          <Route path="/collab" element={<Collab />} />
          <Route path="/collab/:roomId" element={<Collab />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/coaching" element={<Coaching />} />
          <Route path="/intelligence" element={<Intelligence />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/points" element={<Points />} />
          <Route path="/polls" element={<Polls />} />
          <Route path="/giveaways" element={<Giveaways />} />
          <Route path="/contests" element={<Contests />} />
          <Route path="/media-requests" element={<MediaRequests />} />
          <Route path="/goals" element={<StreamGoals />} />
          <Route path="/loyalty" element={<Loyalty />} />
          <Route path="/counters" element={<Counters />} />
          <Route path="/multistream" element={<Multistream />} />
          <Route path="/emotes" element={<Emotes />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/chatbot" element={<ChatBot />} />
          <Route path="/moderation" element={<Moderation />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/webhooks" element={<Webhooks />} />
          <Route path="/developer/webhooks" element={<Webhooks />} />
          <Route path="/tips" element={<TipHistory />} />
          <Route path="/chat-overlay" element={<ChatOverlay />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/subscribers" element={<Subscribers />} />
          <Route path="/stream-keys" element={<StreamKeys />} />
          <Route path="/embed-code" element={<EmbedCode />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/clips" element={<Clips />} />
          <Route path="/clips/import" element={<ClipImport />} />
          <Route path="/vods" element={<Vods />} />
          <Route path="/overlays" element={<OverlayManager />} />
          <Route path="/overlay-editor" element={<OverlayEditor />} />
          <Route path="/widgets" element={<WidgetBuilder />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/themes" element={<Themes />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/discord" element={<Discord />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/merch" element={<MerchStore />} />
          <Route path="/music" element={<Music />} />
          <Route path="/hotkeys" element={<Hotkeys />} />
          <Route path="/raids" element={<Raids />} />
          <Route path="/dashboard/raids" element={<RaidTools />} />
          <Route path="/badges" element={<Badges />} />
          <Route path="/watchparty" element={<WatchParty />} />
          <Route path="/co-streaming" element={<CoStreaming />} />
          <Route path="/co-streaming/:partyId" element={<CoStreaming />} />
          <Route path="/soundboard" element={<Soundboard />} />
          <Route path="/events" element={<RecentEvents />} />
          <Route path="/commands" element={<ChatCommands />} />
          <Route path="/timers" element={<ChatTimers />} />
          <Route path="/automod" element={<AutoMod />} />
          <Route path="/viewer-roles" element={<ViewerRoles />} />
          <Route path="/follows" element={<Follows />} />
          <Route path="/import-export" element={<ImportExport />} />
          <Route path="/settings/data" element={<DataExport />} />
          <Route path="/data-export" element={<DataExport />} />
          <Route path="/stream-info" element={<StreamInfo />} />
          <Route path="/redemptions" element={<Redemptions />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/viewer-queue" element={<ViewerQueue />} />
          <Route path="/chat-stats" element={<ChatStats />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/stream-report" element={<StreamReport />} />
          <Route path="/stream-report/:sessionId" element={<StreamReport />} />
          <Route path="/discover" element={<Discover />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <AppRoutes />
              <CookieConsent />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </I18nProvider>
  )
}
